import {
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  File,
  Folder,
  Grid3X3,
  HardDrive,
  List,
  Search,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import { useMonitorStore } from '@/stores/monitor-store'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / 1024 ** i).toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

function isImageKey(key: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|svg|avif)$/i.test(key)
}

function getFileName(key: string): string {
  const parts = key.split('/')
  return parts[parts.length - 1] || key
}

export function StorageBrowser() {
  useAuth()
  const {
    storageObjects,
    storagePrefixes,
    storageTruncated,
    isLoadingStorage,
    currentPrefix,
    storageStats,
    fetchStorage,
    fetchStorageStats,
  } = useMonitorStore()

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchInput, setSearchInput] = useState('')

  useEffect(() => {
    fetchStorage('')
    fetchStorageStats()
  }, [fetchStorage, fetchStorageStats])

  const breadcrumbs = currentPrefix ? currentPrefix.split('/').filter(Boolean) : []

  const navigateToPrefix = (prefix: string) => {
    fetchStorage(prefix)
  }

  const navigateUp = () => {
    const parts = currentPrefix.split('/').filter(Boolean)
    parts.pop()
    const newPrefix = parts.length > 0 ? `${parts.join('/')}/` : ''
    fetchStorage(newPrefix)
  }

  const handleSearch = () => {
    if (searchInput.trim()) {
      fetchStorage(`${searchInput.trim()}/`)
    }
  }

  const handleLoadMore = () => {
    fetchStorage(currentPrefix, true)
  }

  return (
    <AppShell title="Storage Browser">
      <div className="space-y-5">
        {storageStats && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg border border-dashed border-border bg-surface p-4">
              <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                Total Files
              </p>
              <p className="mt-1.5 font-mono text-xl font-semibold tabular-nums text-ink">
                {formatNumber(storageStats.totalObjects)}
              </p>
            </div>
            <div className="rounded-lg border border-dashed border-border bg-surface p-4">
              <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                Total Size
              </p>
              <p className="mt-1.5 font-mono text-xl font-semibold tabular-nums text-ink">
                {formatBytes(storageStats.totalSize)}
              </p>
            </div>
            <div className="col-span-2 rounded-lg border border-dashed border-border bg-surface p-4">
              <p className="mb-2 font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                Folders
              </p>
              <div className="flex flex-wrap gap-2">
                {storageStats.folders
                  .sort((a, b) => b.totalSize - a.totalSize)
                  .slice(0, 6)
                  .map((folder) => (
                    <span
                      key={folder.name}
                      className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border bg-surface-alt px-2.5 py-1 font-mono text-[10px] text-ink-muted"
                    >
                      <Folder className="h-3 w-3 text-accent" />
                      {folder.name}
                      <span className="text-ink-faint">
                        {folder.fileCount} &middot; {formatBytes(folder.totalSize)}
                      </span>
                    </span>
                  ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by user ID..."
                className="w-56 rounded-md border border-dashed border-border bg-surface py-2 pl-8 pr-3 font-mono text-[11px] text-ink placeholder:text-ink-faint"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            >
              Browse
            </button>
          </div>

          <div className="ml-auto flex rounded-md border border-dashed border-border bg-surface">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={cn(
                'rounded-l-md p-2 transition-colors',
                viewMode === 'grid'
                  ? 'bg-accent/10 text-accent'
                  : 'text-ink-muted hover:bg-surface-alt'
              )}
            >
              <Grid3X3 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={cn(
                'rounded-r-md p-2 transition-colors',
                viewMode === 'list'
                  ? 'bg-accent/10 text-accent'
                  : 'text-ink-muted hover:bg-surface-alt'
              )}
            >
              <List className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {currentPrefix && (
            <button
              type="button"
              onClick={navigateUp}
              className="flex items-center gap-1 rounded-md border border-dashed border-border bg-surface px-2 py-1.5 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            >
              <ArrowLeft className="h-3 w-3" />
              Back
            </button>
          )}

          <div className="flex items-center gap-1 font-mono text-[11px]">
            <button
              type="button"
              onClick={() => navigateToPrefix('')}
              className="text-accent hover:underline"
            >
              <HardDrive className="inline h-3 w-3" /> root
            </button>
            {breadcrumbs.map((crumb, i) => {
              const prefix = `${breadcrumbs.slice(0, i + 1).join('/')}/`
              return (
                <span key={prefix} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 text-ink-faint" />
                  <button
                    type="button"
                    onClick={() => navigateToPrefix(prefix)}
                    className={cn(
                      'transition-colors',
                      i === breadcrumbs.length - 1
                        ? 'text-ink font-medium'
                        : 'text-accent hover:underline'
                    )}
                  >
                    {crumb}
                  </button>
                </span>
              )
            })}
          </div>
        </div>

        {isLoadingStorage ? (
          <div className="flex items-center justify-center py-24">
            <p className="font-mono text-xs text-ink-faint animate-pulse">Loading files...</p>
          </div>
        ) : (
          <>
            {storagePrefixes.length > 0 && (
              <div className="rounded-lg border border-dashed border-border bg-surface">
                <div className="border-b border-dashed border-border px-4 py-2.5">
                  <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    Folders
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-px sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {storagePrefixes.map((prefix) => {
                    const name = prefix.replace(currentPrefix, '').replace(/\/$/, '')
                    return (
                      <button
                        key={prefix}
                        type="button"
                        onClick={() => navigateToPrefix(prefix)}
                        className="flex items-center gap-2.5 p-3 text-left transition-colors hover:bg-surface-alt"
                      >
                        <Folder className="h-5 w-5 shrink-0 text-accent" strokeWidth={1.5} />
                        <span className="truncate font-mono text-xs text-ink">{name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {storageObjects.length > 0 && (
              <div className="rounded-lg border border-dashed border-border bg-surface">
                <div className="border-b border-dashed border-border px-4 py-2.5">
                  <p className="font-mono text-[10px] tracking-wider text-ink-faint uppercase">
                    Files ({storageObjects.length})
                  </p>
                </div>

                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 gap-px sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                    {storageObjects.map((obj) => (
                      <a
                        key={obj.key}
                        href={obj.publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex flex-col p-3 transition-colors hover:bg-surface-alt"
                      >
                        {isImageKey(obj.key) ? (
                          <div className="relative mb-2 aspect-square w-full overflow-hidden rounded border border-border bg-canvas">
                            <img
                              src={obj.publicUrl}
                              alt={getFileName(obj.key)}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-ink/30 opacity-0 transition-opacity group-hover:opacity-100">
                              <ExternalLink className="h-5 w-5 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="mb-2 flex aspect-square w-full items-center justify-center rounded border border-dashed border-border bg-canvas">
                            <File className="h-8 w-8 text-ink-faint" strokeWidth={1} />
                          </div>
                        )}
                        <p
                          className="truncate font-mono text-[10px] text-ink"
                          title={getFileName(obj.key)}
                        >
                          {getFileName(obj.key)}
                        </p>
                        <p className="font-mono text-[9px] text-ink-faint">
                          {formatBytes(obj.size)} &middot; {formatDate(obj.uploaded)}
                        </p>
                      </a>
                    ))}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-dashed border-border">
                        {['', 'File', 'Size', 'Uploaded', ''].map((col) => (
                          <th
                            key={col || 'action'}
                            className="px-4 py-2.5 text-left font-mono text-[10px] font-medium tracking-wider text-ink-faint uppercase"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {storageObjects.map((obj) => (
                        <tr
                          key={obj.key}
                          className="border-b border-dashed border-border-muted last:border-0 transition-colors hover:bg-surface-alt"
                        >
                          <td className="w-12 px-4 py-2.5">
                            {isImageKey(obj.key) ? (
                              <img
                                src={obj.publicUrl}
                                alt=""
                                className="h-8 w-8 rounded border border-border object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <File className="h-4 w-4 text-ink-faint" />
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            <p
                              className="font-mono text-xs text-ink truncate max-w-md"
                              title={obj.key}
                            >
                              {getFileName(obj.key)}
                            </p>
                            <p className="font-mono text-[10px] text-ink-faint truncate max-w-md">
                              {obj.key}
                            </p>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-[11px] tabular-nums text-ink-faint">
                            {formatBytes(obj.size)}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-[11px] text-ink-faint">
                            {formatDate(obj.uploaded)}
                          </td>
                          <td className="px-4 py-2.5">
                            <a
                              href={obj.publicUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded p-1 text-ink-faint transition-colors hover:text-accent"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {storageObjects.length === 0 && storagePrefixes.length === 0 && (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface py-16">
                <HardDrive className="mb-3 h-8 w-8 text-ink-faint" strokeWidth={1} />
                <p className="font-mono text-xs text-ink-faint">No files in this directory</p>
              </div>
            )}

            {storageTruncated && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="rounded-md border border-dashed border-border bg-surface px-4 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
                >
                  Load more files...
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  )
}
